import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { RegulifyService } from "../../api/RegulifyService";
import { useLoading } from "../../context/LoadingContext";
import { useRiskManagerStore } from "../../store/useRiskManagerStore";
import Table from "../Table";
import { fetchAuthSession } from "aws-amplify/auth";
import { buildWsUrl } from "../../utils/wsUrl";

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor((row) => row.r_0, {
    id: "r_0",
    cell: (info) => (
      <div className={`w-full text-center`}>{info.getValue()}</div>
    ),
    header: () => <span />,
    meta: {
      className: "max-w-32 min-w-32",
    },
  }),
  columnHelper.accessor((row) => row.r_1, {
    id: "r_1",
    cell: (info) => (
      <div
        className={`badge w-full text-center ${info.row.index > 0 ? "badge-success" : "badge-info"}`}
      >
        {info.getValue()}
      </div>
    ),
    header: () => <span />,
    footer: (info) => "S1",
    meta: {
      className: "max-w-32 min-w-32",
    },
  }),
  columnHelper.accessor((row) => row.r_2, {
    id: "r_2",
    cell: (info) => (
      <div
        className={`badge w-full text-center ${info.row.index < 2 ? "badge-warning" : "badge-info"}`}
      >
        {info.getValue()}
      </div>
    ),
    header: () => <span />,
    footer: (info) => "S2",
    meta: {
      className: "max-w-32 min-w-32",
    },
  }),
  columnHelper.accessor((row) => row.r_3, {
    id: "r_3",
    cell: (info) => (
      <div
        className={`badge w-full text-center ${info.row.index < 3 ? (info.row.index > 0 ? "badge-warning" : "badge-error") : "badge-info"}`}
      >
        {info.getValue()}
      </div>
    ),
    header: () => <span />,
    footer: (info) => "S3",
    meta: {
      className: "max-w-32 min-w-32",
    },
  }),
  columnHelper.accessor((row) => row.r_4, {
    id: "r_4",
    cell: (info) => (
      <div
        className={`badge w-full text-center ${info.row.index > 0 ? "badge-warning" : "badge-error"}`}
      >
        {info.getValue()}
      </div>
    ),
    header: () => <span />,
    footer: (info) => "S4",
    meta: {
      className: "max-w-32 min-w-32",
    },
  }),
  columnHelper.accessor((row) => row.r_5, {
    id: "r_5",
    cell: (info) => (
      <div
        className={`badge w-full text-center ${info.row.index < 2 ? "badge-error" : "badge-warning"}`}
      >
        {info.getValue()}
      </div>
    ),
    header: () => <span />,
    footer: (info) => "S5",
    meta: {
      className: "max-w-32 min-w-32",
    },
  }),
];

const WS_PING_INTERVAL_MS = 20000;

const WS_ERROR_MESSAGE = "There was a problem processing your Risk Analysis. Please try again later.";

const UNHANDELLED_ERROR_MESSAGE = "Unhandelled Error fulfilling the request: "

const WS_COMPUTET_OCC_SEVRITY_URL = (token) =>  buildWsUrl("compute_occurence_severity", token);

const WS_URL_MATRICE = (token) =>  buildWsUrl("get_occurrence_severity_matrix", token);


const getAuthToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

const startKeepAlive = (ws) =>
  setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "ping" }));
    }
  }, WS_PING_INTERVAL_MS);

const transformMatrix = (data) =>
  ["O5", "O4", "O3", "O2", "O1"].map((oKey) => {
    const row = data[oKey] || {};
    return {
      r_0: oKey,
      r_1: row["S1"] ?? "O",
      r_2: row["S2"] ?? "O",
      r_3: row["S3"] ?? "O",
      r_4: row["S4"] ?? "O",
      r_5: row["S5"] ?? "O",
    };
  });

function handleParsedMessage(parsed, { setLoading }) {
  switch (parsed.type) {
    case "error":
      console.error("Processing error:", parsed.message);
      toast.error(`Error: ${parsed.message}`);
      setLoading(false);
      return true;

    case "info":
      console.info("Processing update:", parsed.message);
      toast.info(`Update: ${parsed.message}`);
      return true;

    case "pong":
      console.info("Received ping/pong reply:", parsed.message);
      return true;

    default:
      return false;
  }
}

const RiskAnalysis = ({ enableNextTab }) => {
  const { setLoading } = useLoading();
  const [selectedRiskFiles, setSelectedRiskFiles] = useState([]);
  const form = useRiskManagerStore(useShallow((state) => state.form));
  const [occurrenceSeverityMatrix, setOccurrenceSeverityMatrix] = useState();
  const [isEnabled, setIsEnabled] = useState(false);

  const getSeverityMatrix = async () => {
    let keepAliveInterval;

    try {
      const token = await getAuthToken();
      
      setLoading(true, "Processing your file… This may take several minutes. Thank you for waiting.", "text-green-600");

      const ws = new WebSocket(WS_URL_MATRICE(token));
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        keepAliveInterval = startKeepAlive(ws);

        const payload = {
          device_name: form["device_name"],
          device_type: form["device_type"],
          residual_matrix_flag: 0,
          override_flag: 1,
        };

        ws.send(JSON.stringify(payload));
        toast.info("Processing started...");
      };

      ws.onmessage = (event) => {
        const { data } = event;

        try {
          const parsed = JSON.parse(data);

          if (handleParsedMessage(parsed, { setLoading })) { return; }

          if (parsed){
            const transformed = transformMatrix(parsed);
            setOccurrenceSeverityMatrix(transformed);
            enableNextTab();
          } else{
             console.log("Unexpected message format:", parsed);
          }
        } catch {
          console.log("Non-JSON message received:", data);
        }
      };

      ws.onerror = () => {
        console.error(WS_ERROR_MESSAGE);
        toast.error(WS_ERROR_MESSAGE);
        setLoading(false);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        clearInterval(keepAliveInterval);
        setLoading(false);
      };
    } catch (error) {
      console.error(UNHANDELLED_ERROR_MESSAGE, error);
	    setLoading(false);
    }
  };

  const generateRiskAnalysisFileClick = async () => {
    if (!selectedRiskFiles?.length) {
      toast.warning("Please select at least one file.");
      return;
    }

    const formData = new FormData();
    for (let i = 0; i < selectedRiskFiles.length && i < 5; i++) {
      formData.append("updatedRiskFile", selectedRiskFiles[i]);
    }

    formData.append("llm_to_be_used", "gpt");
    formData.append("device_name", form["device_name"]);
    formData.append("device_code", form["device_code"]);
    formData.append("device_type", form["device_type"]);
    formData.append("device_usage", form["device_usage"]);
    formData.append("override_type", 1);

    let docPath;

    try {
      setLoading(true, "Processing your file… This may take several minutes. Thank you for waiting.", "text-green-600");

      const response =
        await RegulifyService.riskmgr_upload_override_files(formData);

      if (response.status !== 200) {
        throw new Error("Failed to upload document. Try again.");
      }

      docPath = response.data.risk_file_override_path;
    } catch (error) {
      setLoading(false);
      console.error("Error during uploading file:", error);
      toast.error("Error during uploading file");
      return;
    }

    if (!docPath) return;

    let keepAliveInterval;

    try {
      const token = await getAuthToken();
      const ws = new WebSocket(WS_COMPUTET_OCC_SEVRITY_URL(token));

      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        keepAliveInterval = startKeepAlive(ws);

        const payload = {
          device_code: form["device_code"],
          residual_matrix_flag: 0,
          override_flag: 1,
          device_name: form["device_name"],
          device_usage: form["device_usage"],
          device_type: form["device_type"],
        };

        ws.send(JSON.stringify(payload));
        toast.info("Processing started...");
      };

      ws.onmessage = (event) => {
        const { data } = event;

        try {
          const parsed = JSON.parse(data);
          
          if (handleParsedMessage(parsed, { setLoading })) {  return;}      

          if (parsed.content && parsed.filename) {
            const byteCharacters = atob(parsed.content);
            const byteArray = new Uint8Array(
              [...byteCharacters].map((c) => c.charCodeAt(0)),
            );

            const blob = new Blob([byteArray], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = parsed.filename;
            link.click();
          } else {
            console.log("Unexpected output content received:", parsed);
          }
        } catch {
          console.log("Non-JSON message received:", data);
        }
      };

      ws.onerror = () => {
        console.error(WS_ERROR_MESSAGE);
        toast.error(WS_ERROR_MESSAGE);
        setLoading(false);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        clearInterval(keepAliveInterval);
        setLoading(false);
        setIsEnabled(true);
      };
    } catch (error) {
      console.error(UNHANDELLED_ERROR_MESSAGE, error);
      setLoading(false);
    }
  };

  const occurrenceSeverityTable = useReactTable({
    data: occurrenceSeverityMatrix || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="pl-4">
      {occurrenceSeverityMatrix && (
        <div className="mb-4">
          <Table table={occurrenceSeverityTable} />
        </div>
      )}

      <div className="mt-4 flex flex-col gap-4">
        <div className="form-control w-full max-w-md">
          <label className="label">
            <span className="label-text font-semibold">
              Select Risk Table XLX file (generated in previous step)
            </span>
          </label>
          <input
            type="file"
            accept=".xlsx"
            id="updatedRiskFile"
            className="file-input file-input-bordered w-full"
            onChange={(e) => setSelectedRiskFiles(e.target.files)}
          />
          <p className="mt-1 mb-1 text-sm text-gray-500">
            Accepted formats: <span className="font-medium">.xlsx</span>
          </p>
        </div>
        <button
          className="btn btn-primary w-fitmt-4 tooltip tooltip-right w-fit rounded px-4 py-2"
          data-tip="Submit the hazard file from the previous step to proceed the analysis step"
          onClick={generateRiskAnalysisFileClick}
        >
          Estimate Risk
        </button>
        <button
          className="btn btn-primary w-fitmt-4 tooltip tooltip-right w-fit rounded px-4 py-2"
          data-tip="Submit to create risk matrix graph once analysis file is computed"
          onClick={getSeverityMatrix}
          disabled={!isEnabled}
        >
          Generate Risk Matrix
        </button>
        <p className="text-sm text-gray-500">
          Processing may take up to 30 minutes, depending on product
          specification file contents. Check your downloads folder for the risk
          estimation output file.
        </p>
      </div>
    </div>
  );
};
export default RiskAnalysis;
